import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { plans } from '@/lib/client';
import { describeWhen } from '@/lib/plans';
import { usePlanStream } from '@/hooks/usePlanStream';
import { usePlansStore } from '@/stores/plansStore';
import { ChannelBadge, StatusChip } from '@/components/changes/badges';
import { DocumentUpload } from '@/components/changes/DocumentUpload';
import { NewPlanPrompt } from '@/components/changes/NewPlanPrompt';
import { RecentDocuments } from '@/components/changes/RecentDocuments';

const FILTERS: Array<{ value: plans.PlanStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'ready', label: 'Ready' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Done' },
  { value: 'failed', label: 'Failed' },
];

/** The Changes tab (D18): the tenant's plans, a prompt box to draft a new one, and a document to start from. */
export function ChangesPage() {
  const navigate = useNavigate();
  const { list, listTotal, listLoading, listFilter, loadList, loadMore, error, clearError } = usePlansStore();
  usePlanStream(null);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Changes</h1>
        <p className="text-sm text-muted-foreground">Plans ZAC drafts for your Zoom account: review, edit, approve, and watch them run.</p>
      </div>
      <NewPlanPrompt />
      <DocumentUpload />
      <RecentDocuments />
      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          <span>{error}</span>
          <button onClick={clearError} className="text-xs underline">dismiss</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => void loadList(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${listFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-border bg-card">
        {listLoading && list.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading plans…</div>
        ) : list.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No plans yet. Describe a change above, or ask ZAC in the chat.</div>
        ) : (
          <ul className="divide-y divide-border/60">
            {list.map((plan) => (
              <li key={plan.id}>
                <button onClick={() => navigate(`/changes/${plan.id}`)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-secondary/40">
                  <StatusChip status={plan.status} className="w-28 justify-center" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{plan.title}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{plan.step_count} step{plan.step_count === 1 ? '' : 's'}</span>
                      {plan.channels.map((channel) => <ChannelBadge key={channel} channel={channel} />)}
                      <span>· v{plan.version}</span>
                      <span>· by {plan.created_by.email}</span>
                      {plan.approved_by && <span>· approved by {plan.approved_by.email}</span>}
                      {plan.status === 'scheduled' && plan.scheduled_at && <span>· runs {new Date(plan.scheduled_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{describeWhen(plan.updated_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {list.length > 0 && list.length < listTotal && (
          <div className="border-t border-border/60 p-3 text-center">
            <button onClick={() => void loadMore()} disabled={listLoading} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-50">
              {listLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Show more ({listTotal - list.length} left)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
